import type { ParamSpecs } from '@flobi/artwork-core/plugin';
import { artworkCmd } from '../../services/apiClient';

/**
 * The add-on commands, as the editor calls them.
 *
 * Code comes back as a string inside JSON, which is the only way it ever arrives: there
 * is no URL an add-on can be fetched from and nothing here puts one in a script tag. The
 * sandbox is the only thing that turns it into running code.
 */

export interface AddonSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly kind: 'command' | 'tool';
  readonly icon: string | null;
  readonly visibility: 'private' | 'workspace' | 'library';
  readonly currentHash: string | null;
  readonly installed: boolean;
  readonly mine: boolean;
  readonly updatedAt: string;
}

export interface AddonManifest {
  readonly name: string;
  readonly description: string;
  readonly kind: 'command' | 'tool';
  readonly icon?: string;
  readonly cursor?: string;
  readonly reads: 'selection' | 'artboard' | 'page';
  readonly params: ParamSpecs;
  /** The declared order of `params`; the stored object comes back re-sorted. */
  readonly paramOrder?: readonly string[];
  readonly handlers: readonly ('hover' | 'down' | 'move' | 'up')[];
}

export interface AddonDetail {
  readonly id: string;
  readonly name: string;
  readonly kind: 'command' | 'tool';
  readonly visibility: string;
  readonly version: { readonly hash: string; readonly code: string; readonly manifest: AddonManifest; readonly apiVersion: number };
  readonly install: { readonly versionHash: string; readonly params: Record<string, unknown> } | null;
}

export const addonsApi = {
  list: (kind?: 'command' | 'tool') => artworkCmd<AddonSummary[]>('artwork.plugin.list', kind ? { kind } : {}),
  get: (pluginId: string, hash?: string) => artworkCmd<AddonDetail>('artwork.plugin.get', { pluginId, ...(hash ? { hash } : {}) }),
  create: (input: { source: string; code: string; manifest: AddonManifest }) =>
    artworkCmd<{ id: string; hash: string }>('artwork.plugin.create', input),
  version: (pluginId: string, input: { source: string; code: string; manifest: AddonManifest }) =>
    artworkCmd<{ id: string; hash: string; reused: boolean }>('artwork.plugin.version', { pluginId, ...input }),
  install: (pluginId: string, params?: Record<string, unknown>) =>
    artworkCmd<{ id: string; hash: string }>('artwork.plugin.install', { pluginId, ...(params ? { params } : {}) }),
  uninstall: (pluginId: string) => artworkCmd<{ id: string }>('artwork.plugin.uninstall', { pluginId }),
  share: (pluginId: string, visibility: 'private' | 'workspace') =>
    artworkCmd<{ id: string }>('artwork.plugin.share', { pluginId, visibility }),
  remove: (pluginId: string) => artworkCmd<{ id: string }>('artwork.plugin.delete', { pluginId }),
  recordRun: (input: { pluginId: string; versionHash: string; documentId: string; runId: string; ops: number; ms: number }) =>
    artworkCmd<{ id: string }>('artwork.plugin.recordRun', input),
};
